import { createTheme, responsiveFontSizes, Theme } from '@mui/material/styles';

export function createAppTheme(mode: 'light' | 'dark'): Theme {
  const isDark = mode === 'dark';

let theme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: 'Vazirmatn, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.3,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.35,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
  },
  palette: {
    mode,
    // CasmaShop rose palette. Brand: rose #E07F98, rose-dark #C25E7E,
    // blush #F8D7DF, cream bg #FFF9F5, plum text #4A2A3A.
    // Contrast (WCAG AA ≥ 4.5:1 for normal text):
    //   white on #E07F98 = 2.74 (fail), white on #C25E7E = 4.04 (fail),
    //   white on #B25373 = 4.80 (pass) → light-mode primary.main is a deepened
    //   rose #B25373 so contained buttons/links/selected text stay readable.
    //   In dark mode #E07F98 on #1e293b = 5.34 and plum text on it = 4.55.
    primary: isDark
      ? { main: '#E07F98', light: '#F8D7DF', dark: '#C25E7E', contrastText: '#4A2A3A' }
      : { main: '#B25373', light: '#E07F98', dark: '#8E3D59', contrastText: '#ffffff' },
    secondary: {
      // #4A2A3A on #E07F98 = 4.55:1 (pass)
      main: '#E07F98',
      light: '#F8D7DF',
      dark: '#C25E7E',
      contrastText: '#4A2A3A',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#0ea5e9',
      light: '#38bdf8',
      dark: '#0284c7',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    text: isDark
      ? { primary: '#f3f4f6', secondary: '#9ca3af', disabled: '#6b7280' }
      : { primary: '#4A2A3A', secondary: '#7A5A68', disabled: '#B8A2AC' },
    background: isDark
      ? { default: '#0f172a', paper: '#1e293b' }
      : { default: '#FFF9F5', paper: '#ffffff' },
    divider: isDark ? '#334155' : '#F1E1E6',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html {
          height: 100%;
          scroll-behavior: smooth;
        }
        body {
          height: 100%;
          direction: rtl;
          font-family: 'Vazirmatn', sans-serif !important;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          font-variant-numeric: tabular-nums !important;
        }
        #root {
          height: 100%;
        }
        * {
          font-family: 'Vazirmatn', sans-serif !important;
          font-variant-numeric: tabular-nums !important;
        }
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: ${isDark ? '#1e293b' : '#f1f1f1'};
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${isDark ? '#475569' : '#c0c0c0'};
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? '#64748b' : '#a0a0a0'};
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          fontWeight: 600,
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: '1rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? '#1e293b' : '#FDF3F5',
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: isDark ? '#f3f4f6' : '#4A2A3A',
            backgroundColor: isDark ? '#1e293b' : '#FDF3F5',
            borderBottom: `2px solid ${isDark ? '#334155' : '#F1E1E6'}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: isDark ? '#1e293b' : '#FDF3F5',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          height: 28,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          gap: 8,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 44,
          padding: '12px 16px',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 8,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: isDark ? 'rgba(224, 127, 152, 0.16)' : 'rgba(178, 83, 115, 0.08)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(224, 127, 152, 0.24)' : 'rgba(178, 83, 115, 0.12)',
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        },
        list: {
          padding: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 2,
          minHeight: 40,
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: isDark ? 'rgba(224, 127, 152, 0.16)' : 'rgba(178, 83, 115, 0.08)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? '#475569' : '#1d1d1f',
          borderRadius: 6,
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
        arrow: {
          color: isDark ? '#475569' : '#1d1d1f',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '12px 16px',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          borderRadius: 12,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
        listbox: {
          padding: 8,
        },
        option: {
          borderRadius: 8,
          marginBottom: 2,
        },
      },
    },
    // RTL-specific overrides for JavaScript-controlled components
    // These cannot be handled by rtlPlugin since they're not CSS
    MuiInputAdornment: {
      styleOverrides: {
        positionStart: {
          marginLeft: 8,
          marginRight: 0,
        },
        positionEnd: {
          marginRight: 8,
          marginLeft: 0,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          marginRight: 0,
          marginLeft: 12,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          textAlign: 'right',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          flexDirection: 'row-reverse',
        },
        selectLabel: {
          marginRight: 8,
          marginLeft: 0,
        },
        displayedRows: {
          marginRight: 'auto',
          marginLeft: 16,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textAlign: 'right',
          marginRight: 0,
          marginLeft: 14,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          marginRight: 8,
          marginLeft: 8,
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        ul: {
          flexDirection: 'row-reverse',
        },
      },
    },
  },
});

  theme = responsiveFontSizes(theme, {
    breakpoints: ['sm', 'md', 'lg'],
    factor: 2,
  });

  return theme;
}

export default createAppTheme('light');
